import React, { useState } from "react";
import SectionCard from "./SectionCard";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import { TrendingUp, Trophy } from "lucide-react";


// simple sparkline component for active topics
const STABLE_YEAR = 2024;

const Sparkline = ({ data = [] }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 60;
  const height = 24;
  
  const pointCoords = data.map((val, idx) => {
    const x = (idx / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  });

  const currentYear = new Date().getFullYear();
  const unstableCount = Math.max(0, currentYear - STABLE_YEAR);
  const stableEndIdx = Math.max(1, data.length - unstableCount);

  const stablePointsStr = pointCoords.slice(0, stableEndIdx).join(" ");
  const dashedPointsStr = unstableCount > 0 && stableEndIdx > 0 
    ? pointCoords.slice(stableEndIdx - 1).join(" ") 
    : "";

  return (
    <svg width={width} height={height} className="overflow-visible stroke-explore fill-none stroke-[2] stroke-linecap-round stroke-linejoin-round">
      <polyline points={stablePointsStr} />
      {dashedPointsStr && <polyline points={dashedPointsStr} strokeDasharray="3 3" />}
    </svg>
  );
};

const ResearchStrengthCard = ({
  data,
  showAll = false,
  isLoading,
  error,
  isHidden,
  onRetry,
  onSeeMore,
  seeMoreText = "View all",
  initialTab = "top_topics",
}) => {
  const [activeTab, setActiveTab] = useState(initialTab || "top_topics");

  React.useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  
  const topTopics = data?.top_topics || [];
  const activeTopics = data?.top_active_topics || [];
  
  const currentList = activeTab === "top_topics" ? topTopics : activeTopics;
  const displayList = showAll ? currentList : currentList.slice(0, 5);
  const maxHIndex = Math.max(...currentList.map(t => t.h_index || 0), 1);

  return (
    <SectionCard
      title="Research Strength"
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={!topTopics.length && !activeTopics.length}
      onRetry={onRetry}
      onSeeMore={onSeeMore ? () => onSeeMore(activeTab) : undefined}
      seeMoreText={seeMoreText}
      headerEnd={
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-[260px]">
          <TabsList className="grid w-full grid-cols-2 p-1 bg-muted/50 rounded-lg">
            <TabsTrigger 
              value="top_topics" 
              className="text-xs data-[state=active]:bg-explore data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all"
            >
              Top Topics
            </TabsTrigger>
            <TabsTrigger 
              value="active_topics" 
              className="text-xs data-[state=active]:bg-explore data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all flex items-center gap-1.5"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              Trending
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
    >
      <div className={`flex flex-col flex-1 gap-3 ${showAll ? "max-h-[460px] overflow-y-auto pr-2" : ""}`}>
        {displayList.map((item, idx) => {
          const rank = item.rank || idx + 1;
          const isTop10 = rank <= 10;
          const barWidth = `${((item.h_index || 0) / maxHIndex) * 100}%`;

          return (
            <div 
              key={item.id || idx} 
              className="flex items-center justify-between p-2.5 h-[72px] rounded-lg border bg-card transition-colors"
            >
              <div className="flex flex-col justify-center gap-1 flex-1 min-w-0 pr-4">
                <p className="font-semibold text-sm truncate" title={item.keyword}>
                  {item.keyword}
                </p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex flex-col gap-0.5 w-24 shrink-0">
                    <span className="flex items-center gap-1">
                      <Trophy className="w-3 h-3 text-explore/80" />
                      <span className="font-medium text-foreground">{item.h_index}</span>
                    </span>
                    <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-explore/70 rounded-full" style={{ width: barWidth }} />
                    </div>
                  </div>
                  <span className="opacity-40">•</span>
                  <span>{item.publication_count?.toLocaleString()} pubs</span>
                </div>
              </div>

              <div className="flex items-center shrink-0">
                <div className="flex flex-col items-end justify-center gap-1 w-20">
                  {activeTab === "top_topics" ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-sm transition-colors ${
                            isTop10 ? "bg-explore text-white" : "bg-muted text-muted-foreground"
                          }`}>
                            #{rank}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Ranked #{rank} nationally</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 text-green-600 font-semibold text-xs">
                        <TrendingUp className="w-3.5 h-3.5" />
                        +{item.growth_rate?.toFixed(1)}%
                      </div>
                      <Sparkline data={item.series} />
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default ResearchStrengthCard;
